import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The editor of a statistics counter.
 *
 * The target date is sent at **noon UTC** rather than at midnight. A bare
 * `YYYY-MM-DD` coerces to midnight UTC, which is still the previous day
 * everywhere west of Greenwich: a countdown set to Christmas would have read
 * "1 day left" on the 25th in Lima. Noon lands on the same calendar day from
 * UTC-11 to UTC+12, so the day the administrator picked is the day the counter
 * counts down to, wherever the server sits.
 */

interface Counter {
  _id: string;
  channelId: string;
  kind: string;
  roleId: string | null;
  targetAt: string | null;
  template: string;
  enabled: boolean;
}

interface EditorPageData {
  counter: Counter;
  guildId: string;
  refreshMinutes: number;
}

export function initStatChannelsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-statchannel-form]');
  if (!form) return;

  const { counter, guildId } = pageData<EditorPageData>();

  fillForm(form, { ...counter, targetAt: dayOf(counter.targetAt) });

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = counter.channelId ? [counter.channelId] : [];

  const role = findSelectMenu(form, 'roleId');
  if (role) role.value = counter.roleId ? [counter.roleId] : [];

  /*
   * Ticked by default on a creation: a counter somebody has just described
   * should work without their having to switch it on. `fillForm` cannot guess
   * it, since the document does not exist yet.
   */
  const enabled = form.elements.namedItem('enabled');
  if (enabled instanceof HTMLInputElement) enabled.checked = counter._id ? counter.enabled : true;

  showFor(form, counter.kind || 'members');
  for (const radio of $$<HTMLInputElement>('input[name="kind"]', form)) {
    radio.addEventListener('change', () => {
      showFor(form, radio.value);
    });
  }

  mountResourceEditor({
    endpoint: 'statchannels',
    listUrl: `/modules/statchannels?guildId=${guildId}`,
    guildId,
    form,
    itemId: counter._id,

    serialize: () => {
      const kind = chosenKind(form);
      const day = text(form, 'targetAt');

      return {
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        kind,
        // Sent only for the counter that reads it: a role left over from an
        // earlier choice would be stored and never used, and would come back
        // the day somebody switched the kind back.
        roleId: kind === 'role' ? (findSelectMenu(form, 'roleId')?.single ?? null) : null,
        targetAt: kind === 'countdown' && day ? `${day}T12:00:00.000Z` : null,
        template: text(form, 'template') || '{count}',
        enabled: checked(form, 'enabled'),
        publish: false,
      };
    },

    labels: {
      saved: ct('statChannelSaved', 'Counter saved.'),
      confirmDelete: ct('confirmDeleteStatChannel', 'Delete this counter?'),
      deleted: ct('statChannelDeleted', 'Counter deleted.'),
    },
  });
}

/** Shows only the field the chosen counter actually reads. */
function showFor(form: HTMLFormElement, kind: string): void {
  for (const block of $$<HTMLElement>('[data-when]', form)) {
    block.hidden = block.dataset.when !== kind;
  }
}

function chosenKind(form: HTMLFormElement): string {
  const picked = $$<HTMLInputElement>('input[name="kind"]', form).find((radio) => radio.checked);
  return picked?.value ?? 'members';
}

/** The `YYYY-MM-DD` a `<input type="date">` expects, from a stored instant. */
function dayOf(value: string | null): string {
  return value ? (value.slice(0, 10) ?? '') : '';
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
